import { Component, inject, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import Order from '../../../smoothr-web-app-core/models/Order';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { PaySessionStatistics } from '../../../smoothr-web-app-core/models/PaySessionStatistics';
import { TipSelectorValue, TipType } from '../../components/tip-selector/tip-selector.component';
import { getAbsolutValueFromTipSelection } from '../../../smoothr-web-app-core/utils/utils';
import PiggyGiftCard from '../../../smoothr-web-app-core/models/PiggyGiftCard';
import { TranslateService } from '@ngx-translate/core';

export interface GiftCardModalResult {
	giftCard: PiggyGiftCard;
	selectedTip: TipSelectorValue;
}

@Component({
	standalone: false,
	selector: 'app-gift-card-modal',
	templateUrl: './gift-card-modal.component.html',
	styleUrls: ['./gift-card-modal.component.scss'],
})
export class GiftCardModalComponent implements OnInit {
	orders: Order[] = [];

	totalPrice = 0;
	formattedTotalPrice = '';
	formattedTips = '';
	selectedTip: TipSelectorValue = {
		value: 0,
		type: TipType.RELATIVE,
		icon: '',
	};
	paySessionStatistics: PaySessionStatistics | null = null;

	loading = false;
	giftCard: PiggyGiftCard | null = null;
	giftCardCode = '';
	giftCardError = '';
	formattedGiftCardBalance = '';

	private repositoryService = inject(RepositoryService);
	private modalCtrl: ModalController = inject(ModalController);
	private translate = inject(TranslateService);

	constructor() {}

	get isValid(): boolean {
		return this.giftCard !== null && this.giftCard.balance >= this.totalPrice;
	}

	get formattedSelected() {
		return this.repositoryService.currencyFormat.format(this.paySessionStatistics?.selectedTotalPrice ?? 0);
	}

	static async show(
		modalCtrl: ModalController,
		paySessionStatistics: PaySessionStatistics | null,
		orders: Order[]
	): Promise<GiftCardModalResult> {
		const modal = await modalCtrl.create({
			component: GiftCardModalComponent,
			cssClass: 'payment-sheet-modal',
			componentProps: {
				orders,
				itemCount: orders.reduce((curr, acc) => {
					return curr + acc.checkoutOrderedArticles.length;
				}, 0),
				paySessionStatistics,
			},
		});

		await modal.present();
		const { data } = await modal.onDidDismiss();
		return data;
	}

	ngOnInit() {
		this.reloadPrices();
	}

	async checkGiftCard() {
		if (!this.giftCardCode.trim()) {
			this.giftCardError = this.translate.instant('giftCard.errorEmpty');
			return;
		}

		this.loading = true;
		this.giftCardError = '';

		try {
			// Call RepositoryService to check giftCard balance
			// Assuming there's a method like: getGiftCardBalance(code: string, venue: Venue)
			this.giftCard = await this.repositoryService.getGiftCard(this.giftCardCode, this.orders[0].venue);
			console.log('GiftCard:', this.giftCard);
			this.formattedGiftCardBalance = this.repositoryService.currencyFormat.format(this.giftCard.balance);

			if (this.giftCard.balance < this.totalPrice) {
				this.giftCardError = this.translate.instant('giftCard.errorInsufficient', { balance: this.formattedGiftCardBalance });
			}
		} catch (error) {
			console.error('Error checking giftcard:', error);
			this.giftCardError = this.translate.instant('giftCard.errorNotFound');
			this.giftCard = null;
		} finally {
			this.loading = false;
		}
	}

	async confirmButton() {
		if (!this.isValid) {
			return;
		}

		await this.modalCtrl.dismiss({
			giftCard: this.giftCard,
			selectedTip: this.selectedTip,
		});
	}

	reloadPrices() {
		const selectedTotalPrice = this.paySessionStatistics?.selectedTotalPrice ?? 0;
		const tipValue = getAbsolutValueFromTipSelection(selectedTotalPrice, this.selectedTip);
		this.totalPrice = selectedTotalPrice + tipValue;
		this.formattedTotalPrice = this.repositoryService.currencyFormat.format(this.totalPrice);
		this.formattedTips = this.repositoryService.currencyFormat.format(tipValue);
	}

	close() {
		this.modalCtrl.dismiss();
	}
}
